/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db } from '../firebase';
import { sendEmailVerification } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import './EmailVerification.css';

function EmailVerification() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [checking, setChecking] = useState(false);
  const [user, setUser] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const verificationHandledRef = useRef(false); // Track if verification has been handled
  const navigate = useNavigate();

  const handleVerifiedUser = useCallback(async () => {
    if (!user || verificationHandledRef.current) {
      console.log('Verification already handled or no user, skipping');
      return;
    }

    verificationHandledRef.current = true; // Set flag immediately
    console.log('Starting handleVerifiedUser');

    try {
      // Update Firestore to mark email as verified
      await updateDoc(doc(db, 'users', user.uid), {
        emailVerified: true,
      });

      console.log('Email verification status updated in Firestore');

      // Set sessionStorage flag BEFORE navigating
      console.log('=== About to set sessionStorage ===');
      sessionStorage.setItem('emailJustVerified', 'true');
      console.log('SessionStorage set. Value:', sessionStorage.getItem('emailJustVerified'));
      console.log('All sessionStorage keys:', Object.keys(sessionStorage));
      
      // Navigate to login page - LoginComponent will handle data loading
      console.log('Navigating to login page...');
      navigate('/');
    } catch (error) {
      console.error('Error updating verification status:', error);
      setMessage('Error completing verification. Please try logging in.');
      verificationHandledRef.current = false; // Reset on error
    }
  }, [user, navigate]);

  // Listen for auth state changes
  useEffect(() => {
    console.log('=== Setting up auth listener ===');
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      console.log('Auth state changed:', currentUser?.email || 'No user');
      setUser(currentUser);
      setAuthInitialized(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    console.log('=== EmailVerification useEffect ===');
    console.log('Auth initialized:', authInitialized);
    console.log('Current user:', user?.email || 'No user');
    console.log('User emailVerified:', user?.emailVerified);
    console.log('Verification handled:', verificationHandledRef.current);
    
    if (!authInitialized) {
      console.log('Waiting for auth to initialize...');
      return;
    }

    if (!user) {
      console.log('No user found after auth initialized, navigating to login');
      navigate('/');
      return;
    }

    if (user.emailVerified && !verificationHandledRef.current) {
      console.log('User email is verified, calling handleVerifiedUser');
      handleVerifiedUser();
    } else {
      console.log('User email not yet verified or already handled');
    }
  }, [user, authInitialized, navigate, handleVerifiedUser]);

  const checkVerification = async () => {
    if (!user) return;

    setChecking(true);
    setMessage('');

    try {
      await user.reload();
      
      if (user.emailVerified) {
        setMessage('✓ Email verified! Redirecting...');
        await handleVerifiedUser();
      } else {
        setMessage('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error checking verification:', error);
      setMessage('Error checking verification status. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const resendVerification = async () => {
    if (!user) return;

    setLoading(true);
    setMessage('');

    try {
      await sendEmailVerification(user, {
        url: `${window.location.origin}/?emailVerified=true`,
        handleCodeInApp: false
      });
      setMessage('✓ Verification email resent! Please check your inbox.');
    } catch (error) {
      console.error('Error resending email:', error);
      
      if (error.code === 'auth/too-many-requests') {
        setMessage('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setMessage('Failed to resend email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

//   const handleLogout = async () => {
//     try {
//       await auth.signOut();
//       navigate('/');
//     } catch (error) {
//       console.error('Error signing out:', error);
//     }
//   };

  if (!user) {
    return null;
  }

  return (
    <div className="email-verification-container">
      <div className="verification-card">
        <h2>Verify Your Email</h2>
        
        <div className="verification-icon">📧</div>
        
        <p className="verification-text">
          We've sent a verification email to:
        </p>
        <p className="user-email">{user.email}</p>
        
        <p className="verification-instructions">
          Please check your inbox and click the verification link to activate your account.
        </p>

        {message && (
          <div className={`message ${message.includes('✓') ? 'success' : 'info'}`}>
            {message}
          </div>
        )}

        <div className="button-group">
          <button 
            onClick={checkVerification} 
            disabled={checking}
            className="primary-button"
          >
            {checking ? 'Checking...' : 'I\'ve Verified My Email'}
          </button>

          <button 
            onClick={resendVerification} 
            disabled={loading}
            className="secondary-button"
          >
            {loading ? 'Sending...' : 'Resend Verification Email'}
          </button>

          {/* <button 
            onClick={handleLogout}
            className="text-button"
          >
            Sign Out
          </button> */}
        </div>

        <div className="help-text">
          <p>Didn't receive the email?</p>
          <ul>
            <li>Check your spam/junk folder</li>
            <li>Make sure {user.email} is correct</li>
            <li>Wait a few minutes and try resending</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;